import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {
    concatMap,
    map, switchMap, take,
    withLatestFrom
} from 'rxjs/operators';
import {AppState} from '@store/state/app.state';
import {Store} from '@ngrx/store';
import * as ShoppingCartActions from '@store/actions/shopping-cart.action';
import * as StorageActions from '@store/actions/storage.action';
import {ShoppingCartService} from '@iw-shop/shopping-cart/services/shopping-cart.service';
import {deleteShoppingCart} from '@store/actions/shopping-cart.action';
import {resetShoppingCart, shoppingCart} from '@store/actions/storage.action';
import {setNotification} from '@store/actions/global.actions';
import {
    ApiShoppingCartsSetResponse,
    ApiShoppingCartsUpdateResponse
} from "@api/shopping-carts";
import {DeliveryTourService} from "@iw-shop/delivery-tour/services/delivery-tour.service";
import {Observable} from "rxjs";
import {getSelectedCustomer} from "@store/selectors/user.selector";
import {setChangePriceGroupTo} from "@store/actions/config.action";

@Injectable()
export class ShoppingCartEffects {

    constructor(
        private actions$: Actions,
        private store: Store<AppState>,
        private shoppingCartService: ShoppingCartService,
        protected deliveryTourService: DeliveryTourService
    ) {
    }

    loadShoppingCart = createEffect(() => this.actions$.pipe(
            ofType(ShoppingCartActions.loadShoppingCart),
            concatMap(a => this.shoppingCartService.getShoppingCart(a.id, a.token)),
            map(r => {
                if (r.result?.shoppingCarts &&
                    r.result.shoppingCarts.data[0]) {
                    return ShoppingCartActions.loadShoppingCartSuccess({shoppingCart: r.result.shoppingCarts.data[0]});
                } else {
                    return ShoppingCartActions.loadShoppingCartFailure();
                }
            })
        )
    );

    loadShoppingCartFailure = createEffect(() => this.actions$.pipe(
            ofType(ShoppingCartActions.loadShoppingCartFailure),
            map(() => {
                return StorageActions.resetShoppingCart();
            })
        )
    );

    setShoppingCartArticles$ = createEffect(() => this.actions$.pipe(
            ofType(ShoppingCartActions.setShoppingCartArticles),
            withLatestFrom(this.store.select(state => state.storage.shoppingCart)),
            concatMap(([a, s]) => {
                let request: Observable<ApiShoppingCartsSetResponse | ApiShoppingCartsUpdateResponse>;
                let dynamicArticles: {
                    amount: number,
                    id: string
                }[] = [];


                this.shoppingCartService.getDeselectedDynamicArticles().map(
                    r => {
                        dynamicArticles.push({amount: 0, id: r.id});
                    }
                );

                // Dynamische Artikel für Tour hinzufügen, damit die Bedingungen der dynamischen Artikel bei jeder Änderung geprüft werden
                this.deliveryTourService.getSelectedDeliveryTour().value?.dynamicArticleIds?.map(
                    r => {
                        dynamicArticles.push({amount: 1, id: r});
                    }
                );

                // Dynamische Artikel für Kunden hinzufügen, damit die Bedingungen der dynamischen Artikel bei jeder Änderung geprüft werden
                this.store.select(getSelectedCustomer).pipe(take(1)).subscribe(
                    c => {
                        c?.dynamicArticleIds?.map(
                            r => {
                                dynamicArticles.push({amount: 1, id: r});
                            }
                        )
                    }
                );

                request = this.shoppingCartService.setShoppingCart(
                    'shoppingCart.effects.setShoppingCartArticles$',
                    {
                        data: {
                            articles: a.articles,
                            dynamicArticles: dynamicArticles
                        }
                    }
                );
                console.log('effects set articles');
                return request;

            }),
            map(r => {
                if (r.result?.shoppingCarts) {
                    return ShoppingCartActions.setShoppingCartArticlesSuccess({
                        shoppingCart: r.result.shoppingCarts.data,
                        notifications: r.result.shoppingCarts.notifications ?? []
                    });
                } else {
                    return ShoppingCartActions.setShoppingCartArticlesFailure();
                }
            })
        )
    );

    setShoppingCartArticlesSuccess = createEffect(() => this.actions$.pipe(
            ofType(ShoppingCartActions.setShoppingCartArticlesSuccess),
            map((r) => {
                // Der Warenkorb hat noch einem Update keine Artikel mehr > Kompletten Warenkorb löschen
                if (r.shoppingCart.count.articles.items == 0) {
                    this.store.dispatch(deleteShoppingCart({id: r.shoppingCart.id, token: r.shoppingCart.token}));
                }
            })
        ), {dispatch: false}
    );

    setShoppingCartDynamicArticles$ = createEffect(() => this.actions$.pipe(
            ofType(ShoppingCartActions.setShoppingCartDynamicArticles),
            concatMap(a => {
                return this.store.select(state => state.storage).pipe(
                    take(1),
                    switchMap(r => {
                            let dynamicArticles: {
                                amount: number,
                                id: string
                            }[] = [];


                            this.shoppingCartService.getDeselectedDynamicArticles().map(
                                r => {
                                    dynamicArticles.push({amount: 0, id: r.id});
                                }
                            );

                            // Dynamische Artikel für Tour hinzufügen, damit die Bedingungen der dynamischen Artikel bei jeder Änderung geprüft werden
                            this.deliveryTourService.getSelectedDeliveryTour().value?.dynamicArticleIds?.map(
                                r => {
                                    dynamicArticles.push({amount: 1, id: r});
                                }
                            );

                            // Dynamische Artikel für Kunden hinzufügen, damit die Bedingungen der dynamischen Artikel bei jeder Änderung geprüft werden
                            this.store.select(getSelectedCustomer).pipe(take(1)).subscribe(
                                c => {
                                    c?.dynamicArticleIds?.map(
                                        r => {
                                            dynamicArticles.push({amount: 1, id: r});
                                        }
                                    )
                                }
                            );

                            a.dynamicArticles.map(da => {
                                    dynamicArticles.push({amount: da.amount, id: da.id})
                                }
                            );

                            let request: Observable<ApiShoppingCartsSetResponse | ApiShoppingCartsUpdateResponse>;

                            request = this.shoppingCartService.setShoppingCart(
                                a.jsonId,
                                {
                                    data: {
                                        dynamicArticles
                                    }
                                }
                            )
                        return request;
                        }
                    ),
                );
            }),
            map(r => {
                if (r.result?.shoppingCarts) {
                    return ShoppingCartActions.setShoppingCartDynamicArticlesSuccess({
                        shoppingCart: r.result.shoppingCarts.data,
                        notifications: r.result.shoppingCarts.notifications ?? []
                    });
                } else {
                    return ShoppingCartActions.setShoppingCartDynamicArticlesFailure();
                }
            })
        )
    );

    refreshShoppingCartArticles$ = createEffect(() => this.actions$.pipe(
            ofType(ShoppingCartActions.refreshShoppingCartArticles),
            concatMap(a => {

                    return this.shoppingCartService.refreshShoppingCartArticles(a.jsonId, a.priceGroups);
                }
            ),
            map(r => {
                if (r.result?.shoppingCarts) {
                    return ShoppingCartActions.refreshShoppingCartSuccess({
                        shoppingCart: r.result.shoppingCarts.data,
                        notifications: r.result.shoppingCarts.notifications ?? []
                    });
                } else {
                    return ShoppingCartActions.refreshShoppingCartFailure();
                }
            })
        )
    );

    deleteShoppingCart$ = createEffect(() => this.actions$.pipe(
            ofType(ShoppingCartActions.deleteShoppingCart),
            map((r) => {
                this.store.dispatch(resetShoppingCart());
                return r;
            }),
            concatMap(r =>
            {
                console.log('effects delete');
                return this.shoppingCartService.deleteShoppingCart(r.id, r.token)
            }

            ),
            map(r => {
                if (r.result?.shoppingCarts.deleted) {
                    return ShoppingCartActions.deleteShoppingCartSuccess();
                } else {
                    return ShoppingCartActions.deleteShoppingCartFailure();
                }
            })
        )
    );

    shoppingCartSuccess$ = createEffect(() => this.actions$.pipe(
            ofType(
                ShoppingCartActions.setShoppingCartArticlesSuccess,
                ShoppingCartActions.setShoppingCartDynamicArticlesSuccess,
                ShoppingCartActions.setShoppingCartVouchersSuccess,
                ShoppingCartActions.refreshShoppingCartSuccess
            ),
            map(r => {
                let priceGroup: string | undefined = undefined;
                if(r?.shoppingCart?.items?.dynamicArticles){
                    r?.shoppingCart?.items?.dynamicArticles.some(a => {
                        if (a.changePriceGroupTo && a.changePriceGroupTo.length > 0) {
                            priceGroup = a.changePriceGroupTo;
                            return true; // this will break the loop when condition is met
                        }
                        return false; // Continue looping
                    });
                }
                this.store.select(state => state.config.changePriceGroupTo)
                    .pipe(take(1))
                    .subscribe(
                        changePriceGroupTo => {
                            if(priceGroup !== changePriceGroupTo){
                                this.store.dispatch(setChangePriceGroupTo({ priceGroup: priceGroup }));
                            }
                        }
                    )
                if (r.notifications.length > 0) {
                    r.notifications.map(n => {
                        this.store.dispatch(setNotification({notification: n.message, notificationType: 'info'}))
                    });

                }
            })
        ), {dispatch: false}
    );

    /* decrasePendingRequests = createEffect(() => this.actions$.pipe(
             ofType(
                 ShoppingCartActions.setShoppingCartArticlesSuccess,
                 ShoppingCartActions.setShoppingCartArticlesFailure,
                 ShoppingCartActions.setShoppingCartDynamicArticlesSuccess,
                 ShoppingCartActions.setShoppingCartDynamicArticlesFailure,
                 ShoppingCartActions.setShoppingCartVouchersSuccess,
                 ShoppingCartActions.updateShoppingCartSuccess,
                 ShoppingCartActions.refreshShoppingCartSuccess,
                 ShoppingCartActions.refreshShoppingCartFailure,
                 ShoppingCartActions.deleteShoppingCartSuccess,
                 ShoppingCartActions.deleteShoppingCartFailure
             ),
             map(r => {
                 let value: number = 0;
                 this.store.select(state => state.shoppingCart).pipe(take(1))
                     .subscribe(
                         r => {
                             value = r.requestsDone;
                         }
                     );
                 return ShoppingCartActions.setRequestsDone({counter: value + 1});
             })
         )
     );*/
}
